import { useState, useEffect, useRef } from "react";
import logo from "./web-studio-zone-logo.png";
// ── Utility: smooth scroll ──────────────────────────────────────────────────
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// ── useInView hook ──────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── useParallax hook ────────────────────────────────────────────────────────
function useParallax(speed = 0.3) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handle = () => setOffset(window.scrollY * speed);
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, [speed]);
  return offset;
}

// ── Data ────────────────────────────────────────────────────────────────────
const NAV_LINKS = ["Home","About","Services","Portfolio","Plans","Offers","Testimonials","Contact"];

const SERVICES = [
  { icon: "✦", title: "Logo Design", desc: "Distinctive brand marks crafted to leave a lasting impression across all media." },
  { icon: "◈", title: "Business Card Design", desc: "Elegant visiting cards that speak volumes before you say a word." },
  { icon: "◉", title: "Brand Identity Design", desc: "Complete visual systems — browser themes, style guides, and brand kits." },
  { icon: "⬡", title: "Shopify Store Setup", desc: "Full dropshipping store setup with premium themes and winning products." },
  { icon: "◆", title: "Product Research & Upload", desc: "Data-driven product curation and bulk upload for maximum conversion." },
  { icon: "◎", title: "Social Media Marketing", desc: "Strategic campaigns that grow your audience and drive real revenue." },
  { icon: "⬟", title: "Website Design & Dev", desc: "Pixel-perfect, performance-optimized websites built for modern browsers." },
];

const PORTFOLIO = [
  { cat: "Branding", title: "NovaSpark Identity", color: "#c8a96e" },
  { cat: "Shopify", title: "LuxeCart Store", color: "#7c9eff" },
  { cat: "Web Design", title: "Apex Agency Site", color: "#e8a598" },
  { cat: "Logo", title: "Zenith Mark", color: "#a8e6cf" },
  { cat: "Shopify", title: "TrendDrop Store", color: "#ffd8a8" },
  { cat: "Branding", title: "Solstice Brand Kit", color: "#c9b8ff" },
];

const TESTIMONIALS = [
  { name: "Rahul Sharma", role: "Shopify Store Owner", text: "Web Studio Zone transformed my dropshipping idea into a full-blown store in days. The design is incredible and sales have been consistent ever since.", stars: 5, avatar: "RS" },
  { name: "Priya Mehta", role: "Startup Founder", text: "The logo they designed instantly elevated my brand. Professional, creative, and incredibly affordable. Highly recommend!", stars: 5, avatar: "PM" },
  { name: "Arjun Singh", role: "E-commerce Entrepreneur", text: "From product research to store launch — they handled everything flawlessly. My ROI in the first month was beyond expectations.", stars: 5, avatar: "AS" },
  { name: "Sneha Patel", role: "Digital Marketer", text: "Their social media marketing package is a gem. Consistent branding across all channels and genuine engagement growth.", stars: 5, avatar: "SP" },
];

const PLANS = [
  {
    name: "Starter",
    price: "₹1,499",
    period: "/month",
    highlight: false,
    features: ["Logo Design (1 concept)", "Business Card Design", "Social Media Kit", "2 Revisions", "Email Support"],
  },
  {
    name: "Growth",
    price: "₹4,999",
    period: "/month",
    highlight: true,
    badge: "Most Popular",
    features: ["Full Brand Identity", "Shopify Store Setup", "10 Products Uploaded", "Social Media Marketing", "Priority Support", "Free Domain Setup"],
  },
  {
    name: "Scale",
    price: "₹9,999",
    period: "/month",
    highlight: false,
    features: ["Everything in Growth", "Custom Website Design", "50 Products + Research", "Ad Campaign Management", "Monthly Reports", "Dedicated Manager"],
  },
];

const WHY = [
  { icon: "◈", title: "Affordable Pricing", desc: "Premium quality at prices built for growing businesses." },
  { icon: "✦", title: "Professional Design", desc: "Every pixel crafted with intention and design excellence." },
  { icon: "⚡", title: "Fast Delivery", desc: "Quick turnarounds without compromising on quality." },
  { icon: "◎", title: "Dedicated Support", desc: "We're with you beyond delivery — always reachable." },
  { icon: "◆", title: "Modern Aesthetic", desc: "Designs that look premium and convert visitors into buyers." },
];

// ── Navbar ──────────────────────────────────────────────────────────────────
function Navbar({ active }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(6,6,18,0.92)" : "transparent",
      backdropFilter: scrolled ? "blur(20px)" : "none",
      borderBottom: scrolled ? "1px solid rgba(200,169,110,0.15)" : "none",
      transition: "all 0.4s ease",
      padding: "0 5%",
    }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 72 }}>
        {/* Logo */}
        <div onClick={() => scrollTo("home")} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
         <img
src={logo}
alt="Web Studio Zone"
style={{ height: 48, width: "auto", objectFit: "contain", display: "block" }}
/>
        </div>

        {/* Desktop Nav */}
        <div style={{ display: "flex", gap: 4, alignItems: "center" }} className="wsz-desktop-nav">
          {NAV_LINKS.map(link => (
            <button key={link} onClick={() => { scrollTo(link.toLowerCase()); setOpen(false); }} style={{
              background: "none", border: "none", cursor: "pointer",
              color: active === link.toLowerCase() ? "#c8a96e" : "rgba(255,255,255,0.75)",
              fontSize: 13.5, fontWeight: 500, letterSpacing: 0.5,
              padding: "6px 14px", borderRadius: 6,
              transition: "all 0.2s",
              fontFamily: "'Inter', sans-serif",
            }}
              onMouseEnter={e => e.target.style.color = "#c8a96e"}
              onMouseLeave={e => e.target.style.color = active === link.toLowerCase() ? "#c8a96e" : "rgba(255,255,255,0.75)"}
            >{link}</button>
          ))}
          <button onClick={() => scrollTo("contact")} style={{
            background: "linear-gradient(135deg, #c8a96e, #f5d98c)", border: "none", cursor: "pointer",
            color: "#060612", fontSize: 13, fontWeight: 700, padding: "9px 22px",
            borderRadius: 25, marginLeft: 8, letterSpacing: 0.5,
            transition: "transform 0.2s, box-shadow 0.2s",
            fontFamily: "'Inter', sans-serif",
          }}
            onMouseEnter={e => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = "0 8px 24px rgba(200,169,110,0.4)"; }}
            onMouseLeave={e => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "none"; }}
          >Get Started</button>
        </div>

        {/* Mobile Hamburger */}
        <button onClick={() => setOpen(!open)} className="wsz-hamburger" style={{
          background: "none", border: "none", cursor: "pointer", color: "#c8a96e", fontSize: 24, display: "none",
        }}>☰</button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div style={{
          background: "rgba(6,6,18,0.98)", backdropFilter: "blur(20px)",
          padding: "20px 5%", borderTop: "1px solid rgba(200,169,110,0.15)",
        }}>
          {NAV_LINKS.map(link => (
            <button key={link} onClick={() => { scrollTo(link.toLowerCase()); setOpen(false); }} style={{
              display: "block", width: "100%", textAlign: "left",
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.8)", fontSize: 15, fontWeight: 500,
              padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.06)",
              fontFamily: "'Inter', sans-serif",
            }}>{link}</button>
          ))}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=Inter:wght@300;400;500;600;700;800&display=swap');
        @media (max-width: 768px) {
          .wsz-desktop-nav { display: none !important; }
          .wsz-hamburger { display: block !important; }
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #060612; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #060612; }
        ::-webkit-scrollbar-thumb { background: #c8a96e; border-radius: 3px; }
      `}</style>
    </nav>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────────
function Hero() {
  const parallax = useParallax(0.25);
  const [typed, setTyped] = useState("");
  const full = "Premium Digital Agency";
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      setTyped(full.slice(0, i + 1));
      i++;
      if (i === full.length) clearInterval(t);
    }, 80);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" style={{
      minHeight: "100vh", position: "relative", overflow: "hidden",
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "#060612",
    }}>
      {/* Animated background orbs */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(200,169,110,0.12) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 80% 80%, rgba(124,158,255,0.08) 0%, transparent 50%)",
        transform: `translateY(${parallax}px)`,
        transition: "transform 0.1s linear",
      }} />

      {/* Grid lines */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(200,169,110,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,110,0.04) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }} />

      {/* Floating orbs */}
      {[
        { size: 400, x: "10%", y: "20%", color: "rgba(200,169,110,0.06)", dur: "12s" },
        { size: 300, x: "70%", y: "60%", color: "rgba(124,158,255,0.05)", dur: "16s" },
        { size: 200, x: "50%", y: "10%", color: "rgba(200,169,110,0.04)", dur: "20s" },
      ].map((orb, i) => (
        <div key={i} style={{
          position: "absolute", borderRadius: "50%",
          width: orb.size, height: orb.size,
          left: orb.x, top: orb.y,
          background: `radial-gradient(circle, ${orb.color}, transparent)`,
          animation: `floatOrb${i} ${orb.dur} ease-in-out infinite alternate`,
        }} />
      ))}

      <style>{`
        @keyframes floatOrb0 { from{transform:translate(0,0)} to{transform:translate(30px,-40px)} }
        @keyframes floatOrb1 { from{transform:translate(0,0)} to{transform:translate(-40px,30px)} }
        @keyframes floatOrb2 { from{transform:translate(0,0)} to{transform:translate(20px,50px)} }
        @keyframes shimmer { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(200,169,110,0.4)} 50%{box-shadow:0 0 0 16px rgba(200,169,110,0)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(-30px)} to{opacity:1;transform:translateX(0)} }
        @keyframes countUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes rotate { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes gradientShift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
      `}</style>

      <div style={{ position: "relative", zIndex: 10, textAlign: "center", padding: "10% 5%", maxWidth: 900, margin: "0 auto" }}>
        {/* Badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: "rgba(200,169,110,0.1)", border: "1px solid rgba(200,169,110,0.3)",
          borderRadius: 50, padding: "8px 20px", marginBottom: 32,
          animation: "fadeUp 0.8s ease forwards",
        }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#c8a96e", animation: "shimmer 2s infinite" }} />
          <span style={{ color: "#c8a96e", fontSize: 13, fontWeight: 600, letterSpacing: 1.5, fontFamily: "'Inter', sans-serif", textTransform: "uppercase" }}>
            {typed}
          </span>
        </div>

        {/* Headline */}
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "clamp(2rem, 6vw, 3rem)",
          fontWeight: 800,
          color: "#fff",
          lineHeight: 1.15,
          marginBottom: 24,
          animation: "fadeUp 0.9s 0.2s ease both",
        }}>
          Premium Website &<br />
          <span style={{
            background: "linear-gradient(135deg, #c8a96e 0%, #f5d98c 50%, #c8a96e 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "gradientShift 4s ease infinite",
          }}>Dropshipping Solutions</span>
          <br />for Your Business Growth
        </h1>

        <p style={{
          color: "rgba(255,255,255,0.6)", fontSize: "clamp(1rem, 2vw, 1.2rem)",
          lineHeight: 1.7, marginBottom: 48, maxWidth: 620, margin: "0 auto 48px",
          fontFamily: "'Inter', sans-serif",
          animation: "fadeUp 1s 0.4s ease both",
        }}>
          We build brands, stores, and digital experiences that turn visitors into loyal customers — beautifully crafted and performance-driven.
        </p>

        {/* CTA Buttons */}
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", animation: "fadeUp 1s 0.6s ease both" }}>
          <button onClick={() => scrollTo("contact")} style={{
            background: "linear-gradient(135deg, #c8a96e, #f5d98c)",
            border: "none", cursor: "pointer",
            color: "#060612", fontSize: 15, fontWeight: 700,
            padding: "16px 40px", borderRadius: 50,
            animation: "pulse 2.5s 1s infinite",
            transition: "transform 0.2s",
            fontFamily: "'Inter', sans-serif", letterSpacing: 0.5,
          }}
            onMouseEnter={e => e.target.style.transform = "scale(1.04)"}
            onMouseLeave={e => e.target.style.transform = "scale(1)"}
          >Get Started →</button>
          <button onClick={() => scrollTo("plans")} style={{
            background: "transparent", border: "1.5px solid rgba(200,169,110,0.5)",
            cursor: "pointer", color: "#c8a96e", fontSize: 15, fontWeight: 600,
            padding: "16px 40px", borderRadius: 50,
            transition: "all 0.3s",
            fontFamily: "'Inter', sans-serif",
          }}
            onMouseEnter={e => { e.target.style.background = "rgba(200,169,110,0.1)"; e.target.style.borderColor = "#c8a96e"; }}
            onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.borderColor = "rgba(200,169,110,0.5)"; }}
          >View Plans</button>
        </div>

        {/* Stats */}
        <div style={{
          display: "flex", gap: "clamp(24px,5vw,60px)", justifyContent: "center", flexWrap: "wrap",
          marginTop: 72, animation: "fadeUp 1s 0.8s ease both",
        }}>
          {[["200+", "Projects Delivered"], ["50+", "Happy Clients"], ["3+", "Years Experience"], ["24/7", "Support"]].map(([n, l]) => (
            <div key={l} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 900, color: "#c8a96e" }}>{n}</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, fontFamily: "'Inter', sans-serif", letterSpacing: 0.5, marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{
        position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer",
      }} onClick={() => scrollTo("about")}>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11, letterSpacing: 2, fontFamily: "'Inter', sans-serif", textTransform: "uppercase" }}>Scroll</span>
        <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, rgba(200,169,110,0.6), transparent)", animation: "shimmer 1.5s infinite" }} />
      </div>
    </section>
  );
}

// ── About ───────────────────────────────────────────────────────────────────
function About() {
  const [ref, visible] = useInView();
  return (
    <section id="about" ref={ref} style={{ padding: "120px 5%", background: "#07071a", position: "relative", overflow: "hidden" }}>
      <div style={{
        position: "absolute", top: -200, right: -200,
        width: 500, height: 500, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(200,169,110,0.05), transparent)",
      }} />
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }} className="wsz-about-grid">
        <style>{`.wsz-about-grid { @media(max-width:768px){grid-template-columns:1fr !important;} }`}</style>

        {/* Left visual */}
        <div style={{
          opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(-50px)",
          transition: "all 0.9s cubic-bezier(0.16,1,0.3,1)",
        }}>
          <div style={{
            position: "relative", borderRadius: 24,
            background: "linear-gradient(135deg, rgba(200,169,110,0.08), rgba(124,158,255,0.05))",
            border: "1px solid rgba(200,169,110,0.15)",
            padding: 40, minHeight: 380,
            display: "flex", flexDirection: "column", justifyContent: "space-between",
          }}>
            <div style={{
              fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 6vw, 5rem)",
              fontWeight: 900, color: "rgba(200,169,110,0.15)", lineHeight: 1,
            }}>WS<br />Zone</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {[["Logo & Branding","◈"],["E-commerce","⬡"],["Web Design","◎"],["Marketing","◆"]].map(([t, icon]) => (
                <div key={t} style={{
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,169,110,0.1)",
                  borderRadius: 12, padding: 16,
                }}>
                  <div style={{ fontSize: 20, color: "#c8a96e", marginBottom: 6 }}>{icon}</div>
                  <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>{t}</div>
                </div>
              ))}
            </div>

            {/* Decorative corner */}
            <div style={{ position: "absolute", top: 20, right: 20, width: 60, height: 60, borderTop: "2px solid rgba(200,169,110,0.3)", borderRight: "2px solid rgba(200,169,110,0.3)", borderRadius: "0 8px 0 0" }} />
            <div style={{ position: "absolute", bottom: 20, left: 20, width: 60, height: 60, borderBottom: "2px solid rgba(200,169,110,0.3)", borderLeft: "2px solid rgba(200,169,110,0.3)", borderRadius: "0 0 0 8px" }} />
          </div>
        </div>

        {/* Right content */}
        <div style={{
          opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(50px)",
          transition: "all 0.9s 0.2s cubic-bezier(0.16,1,0.3,1)",
        }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>About Us</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 900, color: "#fff", lineHeight: 1.2, marginBottom: 24 }}>
            We Build Digital<br /><span style={{ color: "#c8a96e" }}>Experiences That Sell</span>
          </h2>
          <p style={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.8, fontSize: 15.5, fontFamily: "'Inter', sans-serif", marginBottom: 20 }}>
            Web Studio Zone is a full-service digital agency based in India, specializing in brand identity, e-commerce solutions, and digital marketing. We combine creative excellence with technical precision to build businesses online.
          </p>
          <p style={{ color: "rgba(255,255,255,0.5)", lineHeight: 1.8, fontSize: 15, fontFamily: "'Inter', sans-serif", marginBottom: 36 }}>
            Whether you're launching your first Shopify store or refreshing your brand identity, our team delivers premium results at accessible prices — fast, reliable, and always on point.
          </p>

          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <button onClick={() => scrollTo("contact")} style={{
              background: "linear-gradient(135deg, #c8a96e, #f5d98c)", border: "none", cursor: "pointer",
              color: "#060612", fontSize: 14, fontWeight: 700, padding: "14px 32px", borderRadius: 50,
              fontFamily: "'Inter', sans-serif", transition: "transform 0.2s",
            }}
              onMouseEnter={e => e.target.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.target.style.transform = "translateY(0)"}
            >Work With Us</button>
            <a href="tel:+919217033580" style={{
              display: "flex", alignItems: "center", gap: 8,
              color: "rgba(255,255,255,0.6)", fontSize: 14, textDecoration: "none",
              fontFamily: "'Inter', sans-serif", transition: "color 0.2s",
            }}
              onMouseEnter={e => e.target.style.color = "#c8a96e"}
              onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.6)"}
            >📞 +91 9217033580</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Services ─────────────────────────────────────────────────────────────────
function Services() {
  const [ref, visible] = useInView();
  const [hovered, setHovered] = useState(null);
  return (
    <section id="services" ref={ref} style={{ padding: "120px 5%", background: "#060612", position: "relative" }}>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(rgba(200,169,110,0.03) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />
      <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative" }}>
        {/* Heading */}
        <div style={{ textAlign: "center", marginBottom: 72, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transition: "all 0.8s ease" }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>What We Do</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#fff" }}>
            Our <span style={{ color: "#c8a96e" }}>Services</span>
          </h2>
        </div>

        {/* Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(40px)",
                transition: `all 0.7s ${i * 0.08}s ease`,
                background: hovered === i
                  ? "linear-gradient(135deg, rgba(200,169,110,0.12), rgba(200,169,110,0.06))"
                  : "rgba(255,255,255,0.03)",
                border: `1px solid ${hovered === i ? "rgba(200,169,110,0.4)" : "rgba(255,255,255,0.06)"}`,
                borderRadius: 20, padding: "36px 32px",
                cursor: "pointer",
                boxShadow: hovered === i ? "0 20px 60px rgba(200,169,110,0.1)" : "none",
                transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)",
              }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: hovered === i ? "linear-gradient(135deg, #c8a96e, #f5d98c)" : "rgba(200,169,110,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22, marginBottom: 20, transition: "all 0.3s",
                color: hovered === i ? "#060612" : "#c8a96e",
              }}>{s.icon}</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 12 }}>{s.title}</h3>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14.5, lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>{s.desc}</p>
              <div style={{
                marginTop: 24, display: "flex", alignItems: "center", gap: 6,
                color: "#c8a96e", fontSize: 13, fontWeight: 600, fontFamily: "'Inter', sans-serif",
                opacity: hovered === i ? 1 : 0, transform: hovered === i ? "translateX(0)" : "translateX(-10px)",
                transition: "all 0.3s",
              }}>Learn more →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Portfolio ────────────────────────────────────────────────────────────────
function Portfolio() {
  const [ref, visible] = useInView();
  const [hovered, setHovered] = useState(null);
  return (
    <section id="portfolio" ref={ref} style={{ padding: "120px 5%", background: "#07071a" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72, opacity: visible ? 1 : 0, transition: "all 0.8s" }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>Our Work</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#fff" }}>
            Featured <span style={{ color: "#c8a96e" }}>Portfolio</span>
          </h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 15.5, fontFamily: "'Inter', sans-serif", maxWidth: 520, margin: "16px auto 0" }}>
            A showcase of brands, stores, and digital experiences we've crafted with care.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
          {PORTFOLIO.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.97)",
                transition: `all 0.7s ${i * 0.08}s cubic-bezier(0.16,1,0.3,1)`,
                borderRadius: 20, overflow: "hidden", cursor: "pointer",
                position: "relative", minHeight: 240,
                background: `linear-gradient(135deg, ${p.color}18, ${p.color}08)`,
                border: `1px solid ${p.color}30`,
                boxShadow: hovered === i ? `0 20px 60px ${p.color}20` : "none",
              }}>
              {/* Decorative background */}
              <div style={{
                position: "absolute", inset: 0,
                background: hovered === i ? `radial-gradient(circle at center, ${p.color}15, transparent)` : "transparent",
                transition: "all 0.4s",
              }} />
              <div style={{ position: "relative", padding: 32, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <div>
                  <span style={{
                    display: "inline-block", background: `${p.color}20`, border: `1px solid ${p.color}40`,
                    color: p.color, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase",
                    padding: "4px 12px", borderRadius: 50, fontFamily: "'Inter', sans-serif",
                  }}>{p.cat}</span>
                </div>
                {/* Decorative shape */}
                <div style={{
                  fontSize: "5rem", color: `${p.color}15`, fontFamily: "'Playfair Display', serif",
                  fontWeight: 900, lineHeight: 1,
                  transform: hovered === i ? "scale(1.1) rotate(-3deg)" : "scale(1) rotate(0deg)",
                  transition: "all 0.4s",
                }}>{p.cat[0]}</div>
                <div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{p.title}</h3>
                  <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, fontFamily: "'Inter', sans-serif" }}>View Project →</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 56 }}>
          <button onClick={() => scrollTo("contact")} style={{
            background: "transparent", border: "1.5px solid rgba(200,169,110,0.4)",
            cursor: "pointer", color: "#c8a96e", fontSize: 14, fontWeight: 600,
            padding: "14px 40px", borderRadius: 50, fontFamily: "'Inter', sans-serif",
            transition: "all 0.3s",
          }}
            onMouseEnter={e => { e.target.style.background = "rgba(200,169,110,0.1)"; }}
            onMouseLeave={e => { e.target.style.background = "transparent"; }}
          >Request a Custom Project</button>
        </div>
      </div>
    </section>
  );
}

// ── Plans ────────────────────────────────────────────────────────────────────
function Plans() {
  const [ref, visible] = useInView();
  const [hovered, setHovered] = useState(null);
  return (
    <section id="plans" ref={ref} style={{ padding: "120px 5%", background: "#060612", position: "relative" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72, opacity: visible ? 1 : 0, transition: "all 0.8s" }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>Pricing</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#fff" }}>
            Simple, Transparent <span style={{ color: "#c8a96e" }}>Plans</span>
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, alignItems: "start" }}>
          {PLANS.map((plan, i) => (
            <div key={plan.name}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? `translateY(${plan.highlight ? -12 : 0}px)` : "translateY(40px)",
                transition: `all 0.8s ${i * 0.1}s cubic-bezier(0.16,1,0.3,1)`,
                borderRadius: 24, padding: "40px 36px", position: "relative", overflow: "hidden",
                background: plan.highlight
                  ? "linear-gradient(135deg, rgba(200,169,110,0.15), rgba(200,169,110,0.08))"
                  : "rgba(255,255,255,0.03)",
                border: plan.highlight ? "1.5px solid rgba(200,169,110,0.5)" : "1px solid rgba(255,255,255,0.06)",
                boxShadow: plan.highlight ? "0 30px 80px rgba(200,169,110,0.15)" : "none",
                cursor: "pointer",
              }}>
              {plan.highlight && (
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: "linear-gradient(90deg, #c8a96e, #f5d98c, #c8a96e)",
                }} />
              )}
              {plan.badge && (
                <div style={{
                  position: "absolute", top: 20, right: 20,
                  background: "linear-gradient(135deg, #c8a96e, #f5d98c)",
                  color: "#060612", fontSize: 11, fontWeight: 800,
                  padding: "4px 12px", borderRadius: 50, letterSpacing: 0.5,
                  fontFamily: "'Inter', sans-serif", textTransform: "uppercase",
                }}>{plan.badge}</div>
              )}

              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12 }}>{plan.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 8 }}>
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 48, fontWeight: 900, color: plan.highlight ? "#c8a96e" : "#fff" }}>{plan.price}</span>
                <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, fontFamily: "'Inter', sans-serif" }}>{plan.period}</span>
              </div>
              <div style={{ width: 40, height: 2, background: plan.highlight ? "#c8a96e" : "rgba(255,255,255,0.1)", marginBottom: 28 }} />

              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 14, marginBottom: 36 }}>
                {plan.features.map(f => (
                  <li key={f} style={{ display: "flex", alignItems: "center", gap: 12, color: "rgba(255,255,255,0.7)", fontSize: 14.5, fontFamily: "'Inter', sans-serif" }}>
                    <span style={{ color: "#c8a96e", fontSize: 16, flexShrink: 0 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <button onClick={() => scrollTo("contact")} style={{
                width: "100%", padding: "14px 0", borderRadius: 50, border: "none", cursor: "pointer",
                background: plan.highlight ? "linear-gradient(135deg, #c8a96e, #f5d98c)" : "rgba(255,255,255,0.06)",
                color: plan.highlight ? "#060612" : "#fff",
                fontSize: 14, fontWeight: 700, fontFamily: "'Inter', sans-serif",
                transition: "all 0.3s",
                ...(hovered === i && !plan.highlight ? { background: "rgba(200,169,110,0.1)", color: "#c8a96e" } : {}),
              }}>Get Started</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Offers ───────────────────────────────────────────────────────────────────
function Offers() {
  const [ref, visible] = useInView();
  return (
    <section id="offers" ref={ref} style={{ padding: "120px 5%", background: "#07071a", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 50%, rgba(200,169,110,0.05), transparent 70%)" }} />
      <div style={{ maxWidth: 1000, margin: "0 auto", position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 72, opacity: visible ? 1 : 0, transition: "all 0.8s" }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>Limited Time</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#fff" }}>
            Special <span style={{ color: "#c8a96e" }}>Offers</span>
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 32 }}>
          {/* Offer 1 */}
          <div style={{
            opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(40px)",
            transition: "all 0.8s 0.1s cubic-bezier(0.16,1,0.3,1)",
            borderRadius: 24, overflow: "hidden",
            border: "1px solid rgba(200,169,110,0.25)",
            background: "rgba(255,255,255,0.02)",
          }}>
            <div style={{ background: "linear-gradient(135deg, rgba(200,169,110,0.2), rgba(200,169,110,0.05))", padding: "28px 32px", borderBottom: "1px solid rgba(200,169,110,0.15)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 8 }}>Logo Design</p>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900, color: "#c8a96e" }}>₹500</h3>
                </div>
                <div style={{ background: "rgba(200,169,110,0.15)", border: "1px solid rgba(200,169,110,0.3)", borderRadius: 10, padding: "6px 14px" }}>
                  <span style={{ color: "#c8a96e", fontSize: 12, fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>SPECIAL</span>
                </div>
              </div>
            </div>
            <div style={{ padding: "28px 32px" }}>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
                {["Professional Logo Design", "High Resolution Files", "Social Media Friendly Format"].map(f => (
                  <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(255,255,255,0.7)", fontSize: 14.5, fontFamily: "'Inter', sans-serif" }}>
                    <span style={{ color: "#c8a96e" }}>✦</span>{f}
                  </li>
                ))}
              </ul>
              <button onClick={() => scrollTo("contact")} style={{
                width: "100%", padding: "13px 0", borderRadius: 50, border: "1.5px solid rgba(200,169,110,0.5)",
                background: "transparent", color: "#c8a96e", fontSize: 14, fontWeight: 700,
                cursor: "pointer", fontFamily: "'Inter', sans-serif", transition: "all 0.3s",
              }}
                onMouseEnter={e => { e.target.style.background = "rgba(200,169,110,0.1)"; }}
                onMouseLeave={e => { e.target.style.background = "transparent"; }}
              >Grab This Offer</button>
            </div>
          </div>

          {/* Offer 2 */}
          <div style={{
            opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(40px)",
            transition: "all 0.8s 0.2s cubic-bezier(0.16,1,0.3,1)",
            borderRadius: 24, overflow: "hidden",
            border: "1.5px solid rgba(200,169,110,0.4)",
            background: "rgba(255,255,255,0.02)",
            position: "relative",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, #c8a96e, #f5d98c)" }} />
            <div style={{ background: "linear-gradient(135deg, rgba(200,169,110,0.18), rgba(124,158,255,0.06))", padding: "28px 32px", borderBottom: "1px solid rgba(200,169,110,0.15)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 8 }}>Dropshipping Setup</p>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900, color: "#c8a96e" }}>₹7,999</h3>
                </div>
                <div style={{ background: "linear-gradient(135deg, #c8a96e, #f5d98c)", borderRadius: 10, padding: "6px 14px" }}>
                  <span style={{ color: "#060612", fontSize: 12, fontWeight: 800, fontFamily: "'Inter', sans-serif" }}>🔥 HOT</span>
                </div>
              </div>
            </div>
            <div style={{ padding: "28px 32px" }}>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
                {["Premium Shopify Theme Setup", "Winning Product Research", "Product Upload & Store Design", "Social Media Marketing Setup", "Basic Store Branding"].map(f => (
                  <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(255,255,255,0.7)", fontSize: 14.5, fontFamily: "'Inter', sans-serif" }}>
                    <span style={{ color: "#c8a96e" }}>✦</span>{f}
                  </li>
                ))}
              </ul>
              <button onClick={() => scrollTo("contact")} style={{
                width: "100%", padding: "13px 0", borderRadius: 50, border: "none",
                background: "linear-gradient(135deg, #c8a96e, #f5d98c)", color: "#060612", fontSize: 14, fontWeight: 700,
                cursor: "pointer", fontFamily: "'Inter', sans-serif", transition: "transform 0.2s, box-shadow 0.2s",
              }}
                onMouseEnter={e => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = "0 12px 30px rgba(200,169,110,0.3)"; }}
                onMouseLeave={e => { e.target.style.transform = ""; e.target.style.boxShadow = ""; }}
              >Start My Store Now</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ─────────────────────────────────────────────────────────────
function Testimonials() {
  const [ref, visible] = useInView();
  return (
    <section id="testimonials" ref={ref} style={{ padding: "120px 5%", background: "#060612" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72, opacity: visible ? 1 : 0, transition: "all 0.8s" }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>Client Love</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#fff" }}>
            What Our Clients <span style={{ color: "#c8a96e" }}>Say</span>
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(40px)",
              transition: `all 0.8s ${i * 0.1}s ease`,
              background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 20, padding: "32px 28px",
              backdropFilter: "blur(10px)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", background: "linear-gradient(to bottom, #c8a96e, transparent)" }} />
              <div style={{ display: "flex", marginBottom: 16 }}>
                {Array(t.stars).fill(0).map((_, si) => (
                  <span key={si} style={{ color: "#c8a96e", fontSize: 16 }}>★</span>
                ))}
              </div>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 15, lineHeight: 1.75, fontFamily: "'Inter', sans-serif", marginBottom: 28, fontStyle: "italic" }}>"{t.text}"</p>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: "linear-gradient(135deg, #c8a96e, #f5d98c)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 800, color: "#060612", fontFamily: "'Inter', sans-serif",
                }}>{t.avatar}</div>
                <div>
                  <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>{t.name}</div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, fontFamily: "'Inter', sans-serif" }}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const [ref, visible] = useInView();
  return (
    <section style={{ padding: "120px 5%", background: "#07071a", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 30% 50%, rgba(124,158,255,0.04), transparent 60%)" }} />
      <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative" }} ref={ref}>
        <div style={{ textAlign: "center", marginBottom: 72, opacity: visible ? 1 : 0, transition: "all 0.8s" }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>Our Edge</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#fff" }}>
            Why Choose <span style={{ color: "#c8a96e" }}>Web Studio Zone</span>
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 24 }}>
          {WHY.map((w, i) => (
            <div key={w.title} style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(40px)",
              transition: `all 0.7s ${i * 0.1}s ease`,
              textAlign: "center", padding: "40px 24px",
              borderRadius: 20, border: "1px solid rgba(200,169,110,0.1)",
              background: "rgba(255,255,255,0.02)",
              transition: `all 0.7s ${i * 0.1}s ease`,
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: 18,
                background: "rgba(200,169,110,0.1)", border: "1px solid rgba(200,169,110,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 24, color: "#c8a96e", margin: "0 auto 20px",
              }}>{w.icon}</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 10 }}>{w.title}</h3>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>{w.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA Banner ────────────────────────────────────────────────────────────────
function CTABanner() {
  const parallax = useParallax(0.15);
  const [ref, visible] = useInView();
  return (
    <section ref={ref} style={{ padding: "100px 5%", position: "relative", overflow: "hidden" }}>
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(135deg, #0d0d2b 0%, #1a1230 50%, #0d1a1a 100%)",
        transform: `translateY(${parallax * 0.3}px)`,
      }} />
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(200,169,110,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,110,0.05) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }} />
      <div style={{
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: 600, height: 300, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(200,169,110,0.1), transparent)",
      }} />

      <div ref={ref} style={{
        maxWidth: 800, margin: "0 auto", textAlign: "center", position: "relative",
        opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(40px)",
        transition: "all 1s cubic-bezier(0.16,1,0.3,1)",
      }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.2rem, 5vw, 4rem)", fontWeight: 900, color: "#fff", lineHeight: 1.2, marginBottom: 24 }}>
          Start Your Online<br />
          <span style={{ color: "#c8a96e" }}>Business Today</span>
        </h2>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 16, fontFamily: "'Inter', sans-serif", marginBottom: 48, lineHeight: 1.7 }}>
          Don't wait — your competitors aren't. Let's build something extraordinary together.
        </p>
        <div style={{ display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="tel:+919217033580" style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "linear-gradient(135deg, #c8a96e, #f5d98c)", borderRadius: 50,
            padding: "16px 36px", color: "#060612", fontSize: 15, fontWeight: 700,
            textDecoration: "none", fontFamily: "'Inter', sans-serif",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 30px rgba(200,169,110,0.35)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
          >📞 Call Now</a>
          <button onClick={() => scrollTo("contact")} style={{
            background: "transparent", border: "1.5px solid rgba(200,169,110,0.5)",
            cursor: "pointer", color: "#c8a96e", fontSize: 15, fontWeight: 600,
            padding: "16px 36px", borderRadius: 50, fontFamily: "'Inter', sans-serif",
            transition: "all 0.3s",
          }}
            onMouseEnter={e => e.target.style.background = "rgba(200,169,110,0.1)"}
            onMouseLeave={e => e.target.style.background = "transparent"}
          >Contact Us</button>
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function Contact() {
  const [ref, visible] = useInView();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = (e) => { e.preventDefault(); setSent(true); };

  const inputStyle = {
    width: "100%", padding: "14px 18px",
    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12, color: "#fff", fontSize: 15, fontFamily: "'Inter', sans-serif",
    outline: "none", transition: "border-color 0.3s",
  };

  return (
    <section id="contact" ref={ref} style={{ padding: "120px 5%", background: "#060612", position: "relative" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(200,169,110,0.03) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 72, opacity: visible ? 1 : 0, transition: "all 0.8s" }}>
          <p style={{ color: "#c8a96e", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>Get In Touch</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 900, color: "#fff" }}>
            Let's <span style={{ color: "#c8a96e" }}>Work Together</span>
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 60, alignItems: "start" }} className="wsz-contact-grid">
          <style>{`.wsz-contact-grid{@media(max-width:768px){grid-template-columns:1fr !important;}}`}</style>

          {/* Info */}
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(-40px)", transition: "all 0.9s ease" }}>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 15.5, fontFamily: "'Inter', sans-serif", lineHeight: 1.8, marginBottom: 40 }}>
              Ready to take your business to the next level? Let's chat about your project and find the perfect solution.
            </p>
            {[
              { icon: "📞", label: "Phone / WhatsApp", val: "+91 9217033580", href: "tel:+919217033580" },
              { icon: "🌐", label: "Website", val: "webstudiozone.com", href: "https://webstudiozone.com/" },
              { icon: "✉️", label: "Email", val: "hello@webstudiozone.com", href: "mailto:hello@webstudiozone.com" },
            ].map(c => (
              <a key={c.label} href={c.href} target="_blank" rel="noreferrer" style={{
                display: "flex", alignItems: "center", gap: 16, padding: "16px 20px",
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 14, marginBottom: 12, textDecoration: "none",
                transition: "all 0.3s", cursor: "pointer",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(200,169,110,0.4)"; e.currentTarget.style.background = "rgba(200,169,110,0.06)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
              >
                <span style={{ fontSize: 22 }}>{c.icon}</span>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, fontFamily: "'Inter', sans-serif", marginBottom: 2 }}>{c.label}</div>
                  <div style={{ color: "#fff", fontSize: 15, fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>{c.val}</div>
                </div>
              </a>
            ))}
          </div>

          {/* Form */}
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(40px)", transition: "all 0.9s 0.2s ease" }}>
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(200,169,110,0.15)", borderRadius: 24, padding: "40px 36px" }}>
              {sent ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#c8a96e", marginBottom: 12 }}>Message Sent!</h3>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontFamily: "'Inter', sans-serif" }}>We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: 13, fontFamily: "'Inter', sans-serif", marginBottom: 8 }}>Name</label>
                      <input name="name" value={form.name} onChange={handle} required placeholder="Your name"
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = "rgba(200,169,110,0.5)"}
                        onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                    </div>
                    <div>
                      <label style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: 13, fontFamily: "'Inter', sans-serif", marginBottom: 8 }}>Phone</label>
                      <input name="phone" value={form.phone} onChange={handle} placeholder="+91 XXXXX XXXXX"
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = "rgba(200,169,110,0.5)"}
                        onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: 13, fontFamily: "'Inter', sans-serif", marginBottom: 8 }}>Email</label>
                    <input name="email" type="email" value={form.email} onChange={handle} required placeholder="your@email.com"
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = "rgba(200,169,110,0.5)"}
                      onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: 13, fontFamily: "'Inter', sans-serif", marginBottom: 8 }}>Message</label>
                    <textarea name="message" value={form.message} onChange={handle} required rows={5} placeholder="Tell us about your project..."
                      style={{ ...inputStyle, resize: "vertical", minHeight: 120 }}
                      onFocus={e => e.target.style.borderColor = "rgba(200,169,110,0.5)"}
                      onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                  </div>
                  <button type="submit" style={{
                    width: "100%", padding: "16px 0", borderRadius: 50, border: "none",
                    background: "linear-gradient(135deg, #c8a96e, #f5d98c)", cursor: "pointer",
                    color: "#060612", fontSize: 15, fontWeight: 700,
                    fontFamily: "'Inter', sans-serif", transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                    onMouseEnter={e => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = "0 12px 30px rgba(200,169,110,0.3)"; }}
                    onMouseLeave={e => { e.target.style.transform = ""; e.target.style.boxShadow = ""; }}
                  >Send Message →</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{ background: "#030310", borderTop: "1px solid rgba(200,169,110,0.1)", padding: "72px 5% 32px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 48, marginBottom: 60 }} className="wsz-footer-grid">
          <style>{`.wsz-footer-grid{@media(max-width:768px){grid-template-columns:1fr 1fr !important;} @media(max-width:480px){grid-template-columns:1fr !important;}}`}</style>

          {/* Brand */}
          <div>
            <div style={{ marginBottom: 20 }}>
              <img src="/mnt/user-data/uploads/web-studio-zone-logo.png" alt="Web Studio Zone" style={{ height: 44, width: "auto", objectFit: "contain" }} />
            </div>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, lineHeight: 1.8, fontFamily: "'Inter', sans-serif", maxWidth: 280, marginBottom: 24 }}>
              Premium digital agency crafting brands, stores, and experiences that grow businesses online.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              {["𝕏", "in", "📘", "📸"].map((s, i) => (
                <div key={i} style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", fontSize: 14, color: "rgba(255,255,255,0.5)",
                  transition: "all 0.3s",
                }}
                  onMouseEnter={e => { e.target.style.background = "rgba(200,169,110,0.15)"; e.target.style.color = "#c8a96e"; }}
                  onMouseLeave={e => { e.target.style.background = "rgba(255,255,255,0.05)"; e.target.style.color = "rgba(255,255,255,0.5)"; }}
                >{s}</div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{ color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "'Inter', sans-serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 20 }}>Quick Links</h4>
            <ul style={{ listStyle: "none" }}>
              {NAV_LINKS.map(link => (
                <li key={link} style={{ marginBottom: 10 }}>
                  <button onClick={() => scrollTo(link.toLowerCase())} style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "rgba(255,255,255,0.45)", fontSize: 14, fontFamily: "'Inter', sans-serif",
                    padding: 0, transition: "color 0.2s",
                  }}
                    onMouseEnter={e => e.target.style.color = "#c8a96e"}
                    onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.45)"}
                  >{link}</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 style={{ color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "'Inter', sans-serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 20 }}>Services</h4>
            <ul style={{ listStyle: "none" }}>
              {["Logo Design", "Shopify Setup", "Brand Identity", "Web Design", "SMM", "Product Research"].map(s => (
                <li key={s} style={{ marginBottom: 10, color: "rgba(255,255,255,0.45)", fontSize: 14, fontFamily: "'Inter', sans-serif" }}>{s}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "'Inter', sans-serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 20 }}>Contact</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <a href="tel:+919217033580" style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, fontFamily: "'Inter', sans-serif", textDecoration: "none", display: "flex", alignItems: "center", gap: 8, transition: "color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.color = "#c8a96e"}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
              >📞 +91 9217033580</a>
              <a href="https://webstudiozone.com/" target="_blank" rel="noreferrer" style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, fontFamily: "'Inter', sans-serif", textDecoration: "none", display: "flex", alignItems: "center", gap: 8, transition: "color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.color = "#c8a96e"}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
              >🌐 webstudiozone.com</a>
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, fontFamily: "'Inter', sans-serif" }}>© {new Date().getFullYear()} Web Studio Zone. All rights reserved.</p>
          <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 13, fontFamily: "'Inter', sans-serif" }}>Designed and Developed by <a href="https://webcodes.in/" target="_blank" rel="noreferrer" style={{ color: "rgba(255,255,255,0.2)", textDecoration: "underline" }}>webcodes.in</a></p>
        </div>
      </div>
    </footer>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [activeSection, setActiveSection] = useState("home");
  useEffect(() => {
    const ids = NAV_LINKS.map(l => l.toLowerCase());
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { threshold: 0.3 }
    );
    ids.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  return (
    <div style={{ background: "#060612", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      <Navbar active={activeSection} />
      <Hero />
      <About />
      <Services />
      <Portfolio />
      <Plans />
      <Offers />
      <Testimonials />
      <WhyUs />
      <CTABanner />
      <Contact />
      <Footer />
    </div>
  );
}
